import React from 'react';
import ExercisePracticeLayout from '../components/exercise/ExercisePracticeLayout';
import { Navigate, type RouteObject } from 'react-router-dom';
import { RequireRole, AssignmentFromClassroomRedirect } from './RouteGuards';
import {
  AssignmentCreate,
  AssignmentDetail,
  AssignmentEdit,
  AssignmentItemDetail,
  ClassroomsList,
  ClassroomCreate,
  ContentActivities,
  ContentSourceDetail,
  ContentSources,
  ExerciseLibrary,
  IndexedDocuments,
  PreviewPractice,
  SessionDetail,
  StudentNameManagement,
  StudentPracticeDetail,
  StudentSubmissions,
  TemplateDetail,
  UserDashboard,
} from './lazyPages';

export const teacherDashboardRoutes: RouteObject[] = [
  {
    path: 'users/:id/dashboard',
    element: (
      <RequireRole role="TEACHER">
        <React.Suspense fallback={null}>
          <UserDashboard />
        </React.Suspense>
      </RequireRole>
    ),
  },
];

export const teacherClassroomRoutes: RouteObject[] = [
  {
    path: 'classrooms',
    element: (
      <RequireRole role="TEACHER">
        <React.Suspense fallback={null}>
          <ClassroomsList />
        </React.Suspense>
      </RequireRole>
    ),
  },
  {
    path: 'classrooms/create',
    element: (
      <RequireRole role="TEACHER">
        <React.Suspense fallback={null}>
          <ClassroomCreate />
        </React.Suspense>
      </RequireRole>
    ),
  },
  {
    path: 'classrooms/:code/sessions/detail',
    element: (
      <RequireRole role="TEACHER">
        <React.Suspense fallback={null}>
          <SessionDetail />
        </React.Suspense>
      </RequireRole>
    ),
  },
  { path: 'classrooms/:code/assignments', element: <Navigate to=".." replace /> },
  {
    path: 'classrooms/:code/assignments/:assignmentCode',
    element: <AssignmentFromClassroomRedirect />,
  },
  {
    path: 'classrooms/:code/assignments/create',
    element: (
      <RequireRole role="TEACHER">
        <React.Suspense fallback={null}>
          <AssignmentCreate />
        </React.Suspense>
      </RequireRole>
    ),
  },
  {
    path: 'assignments/create',
    element: (
      <RequireRole role="TEACHER">
        <React.Suspense fallback={null}>
          <AssignmentCreate />
        </React.Suspense>
      </RequireRole>
    ),
  },
  {
    path: 'assignments/:assignmentCode/edit',
    element: (
      <RequireRole role="TEACHER">
        <React.Suspense fallback={null}>
          <AssignmentEdit />
        </React.Suspense>
      </RequireRole>
    ),
  },
  {
    path: 'classrooms/:code/students/names',
    element: (
      <RequireRole role="TEACHER">
        <React.Suspense fallback={null}>
          <StudentNameManagement />
        </React.Suspense>
      </RequireRole>
    ),
  },
  {
    path: 'classrooms/:code/students/:studentId/submissions',
    element: (
      <RequireRole role="TEACHER">
        <React.Suspense fallback={null}>
          <StudentSubmissions />
        </React.Suspense>
      </RequireRole>
    ),
  },
];

export const teacherAssignmentRoutes: RouteObject[] = [
  {
    path: 'assignments/:code',
    element: (
      <RequireRole role="TEACHER">
        <React.Suspense fallback={null}>
          <AssignmentDetail />
        </React.Suspense>
      </RequireRole>
    ),
  },
  {
    path: 'assignments/:code/items/:itemId',
    element: (
      <RequireRole role="TEACHER">
        <React.Suspense fallback={null}>
          <AssignmentItemDetail />
        </React.Suspense>
      </RequireRole>
    ),
  },
  {
    path: 'assignments/:code/students/:studentId/practice',
    element: (
      <RequireRole role="TEACHER">
        <React.Suspense fallback={null}>
          <StudentPracticeDetail />
        </React.Suspense>
      </RequireRole>
    ),
  },
];

export const teacherContentRoutes: RouteObject[] = [
  {
    path: 'content/sources',
    element: (
      <RequireRole role="TEACHER">
        <React.Suspense fallback={null}>
          <ContentSources />
        </React.Suspense>
      </RequireRole>
    ),
  },
  {
    path: 'content/sources/:id',
    element: (
      <RequireRole role="TEACHER">
        <React.Suspense fallback={null}>
          <ContentSourceDetail />
        </React.Suspense>
      </RequireRole>
    ),
  },
  {
    path: 'content/activities',
    element: (
      <RequireRole role="TEACHER">
        <React.Suspense fallback={null}>
          <ContentActivities />
        </React.Suspense>
      </RequireRole>
    ),
  },
];

export const teacherLibraryRoutes: RouteObject[] = [
  {
    path: 'indexed-documents',
    element: (
      <RequireRole role="TEACHER">
        <React.Suspense fallback={null}>
          <IndexedDocuments />
        </React.Suspense>
      </RequireRole>
    ),
  },
];

export const teacherExerciseLibraryRoutes: RouteObject[] = [
  {
    path: 'exercise-library',
    element: (
      <RequireRole role="TEACHER">
        <React.Suspense fallback={null}>
          <ExerciseLibrary />
        </React.Suspense>
      </RequireRole>
    ),
  },
  {
    path: 'exercise-library/tab/:tab',
    element: (
      <RequireRole role="TEACHER">
        <React.Suspense fallback={null}>
          <ExerciseLibrary />
        </React.Suspense>
      </RequireRole>
    ),
  },
  {
    path: 'exercise-library/:groupId',
    element: (
      <RequireRole role="TEACHER">
        <React.Suspense fallback={null}>
          <ExerciseLibrary />
        </React.Suspense>
      </RequireRole>
    ),
  },
  {
    path: 'exercise-library/groups/:groupId/templates/:templateId',
    element: (
      <RequireRole role="TEACHER">
        <React.Suspense fallback={null}>
          <TemplateDetail />
        </React.Suspense>
      </RequireRole>
    ),
  },
  {
    path: 'exercise-library/templates/:templateId',
    element: (
      <RequireRole role="TEACHER">
        <React.Suspense fallback={null}>
          <TemplateDetail />
        </React.Suspense>
      </RequireRole>
    ),
  },
  {
    path: 'preview/practice/:templateId',
    element: (
      <RequireRole role="TEACHER">
        <React.Suspense fallback={null}>
          <ExercisePracticeLayout>
            <PreviewPractice />
          </ExercisePracticeLayout>
        </React.Suspense>
      </RequireRole>
    ),
  },
];
